import * as React from "react";
import { cn } from "lib/utils";
import { tone, type Tone } from "components/ui/tone";

const SIZE = {
    sm: "w-7 h-7 rounded-lg [&>svg]:w-3.5 [&>svg]:h-3.5",
    md: "w-9 h-9 rounded-xl [&>svg]:w-4 [&>svg]:h-4",
    lg: "w-11 h-11 rounded-xl [&>svg]:w-5 [&>svg]:h-5",
    xl: "w-14 h-14 rounded-2xl [&>svg]:w-6 [&>svg]:h-6",
} as const;

export interface IconTileProps extends React.HTMLAttributes<HTMLDivElement> {
    tone?: Tone;
    size?: keyof typeof SIZE;
    /** Filled tile (solid brand/status colour) instead of the soft tint */
    solid?: boolean;
}

/**
 * The rounded, tinted square that sits in front of a title, KPI or empty
 * state. Replaces ~40 hand-rolled `w-10 h-10 bg-blue-50 rounded-xl` blocks.
 */
export const IconTile = React.forwardRef<HTMLDivElement, IconTileProps>(
    ({ tone: t = "brand", size = "md", solid = false, className, children, ...props }, ref) => {
        const tk = tone(t);
        return (
            <div
                ref={ref}
                aria-hidden="true"
                className={cn(
                    "flex-shrink-0 inline-flex items-center justify-center",
                    SIZE[size],
                    solid ? cn(tk.solid, "text-white shadow-xs") : tk.tile,
                    className
                )}
                {...props}
            >
                {children}
            </div>
        );
    }
);
IconTile.displayName = "IconTile";
